import { checkWinner, getAvailableMoves, applyMove, isBoardFull } from './gameLogic';

/**
 * @param {import('./gameLogic').Board} board
 * @param {'X' | 'O'} aiPlayer
 * @returns {number | null}
 */
function findWinningMove(board, aiPlayer) {
  for (const i of getAvailableMoves(board)) {
    const next = applyMove(board, i, aiPlayer);
    const w = checkWinner(next);
    if (w && w.winner === aiPlayer) return i;
  }
  return null;
}

/**
 * @param {import('./gameLogic').Board} board
 * @param {'X' | 'O'} opponent
 */
function findBlockingMove(board, opponent) {
  return findWinningMove(board, opponent);
}

/**
 * @param {import('./gameLogic').Board} board
 */
function randomMove(board) {
  const moves = getAvailableMoves(board);
  if (!moves.length) return null;
  return moves[Math.floor(Math.random() * moves.length)];
}

/**
 * Minimax: +1 AI wins, -1 opponent wins, 0 draw.
 * @param {import('./gameLogic').Board} board
 * @param {'X' | 'O'} aiPlayer
 * @param {boolean} isMaximizing — true when it is AI's turn to move on `board`
 */
function minimaxScore(board, aiPlayer, isMaximizing) {
  const w = checkWinner(board);
  if (w) return w.winner === aiPlayer ? 1 : -1;
  if (isBoardFull(board)) return 0;

  const opponent = aiPlayer === 'X' ? 'O' : 'X';
  const moves = getAvailableMoves(board);
  if (isMaximizing) {
    let best = -2;
    for (const i of moves) {
      const next = applyMove(board, i, aiPlayer);
      const s = minimaxScore(next, aiPlayer, false);
      best = Math.max(best, s);
    }
    return best;
  }
  let best = 2;
  for (const i of moves) {
    const next = applyMove(board, i, opponent);
    const s = minimaxScore(next, aiPlayer, true);
    best = Math.min(best, s);
  }
  return best;
}

/**
 * @param {import('./gameLogic').Board} board
 * @param {'X' | 'O'} aiPlayer
 * @returns {number | null}
 */
export function getHardMove(board, aiPlayer) {
  const moves = getAvailableMoves(board);
  if (!moves.length) return null;
  let bestScore = -2;
  /** @type {number[]} */
  const bestMoves = [];
  for (const i of moves) {
    const next = applyMove(board, i, aiPlayer);
    const w = checkWinner(next);
    if (w && w.winner === aiPlayer) return i;
    const score = minimaxScore(next, aiPlayer, false);
    if (score > bestScore) {
      bestScore = score;
      bestMoves.length = 0;
      bestMoves.push(i);
    } else if (score === bestScore) {
      bestMoves.push(i);
    }
  }
  return bestMoves[Math.floor(Math.random() * bestMoves.length)];
}

/**
 * @param {import('./gameLogic').Board} board
 * @param {'X' | 'O'} aiPlayer
 * @returns {number | null}
 */
export function getEasyMove(board, aiPlayer) {
  return randomMove(board);
}

/**
 * @param {import('./gameLogic').Board} board
 * @param {'X' | 'O'} aiPlayer
 * @returns {number | null}
 */
export function getMediumMove(board, aiPlayer) {
  const opponent = aiPlayer === 'X' ? 'O' : 'X';
  if (Math.random() < 0.38) {
    return randomMove(board);
  }
  const win = findWinningMove(board, aiPlayer);
  if (win !== null) return win;
  const block = findBlockingMove(board, opponent);
  if (block !== null) return block;
  if (Math.random() < 0.22) {
    return randomMove(board);
  }
  return getHardMove(board, aiPlayer);
}

/**
 * @param {import('./gameLogic').Board} board
 * @param {'X' | 'O'} aiPlayer
 * @param {'easy' | 'medium' | 'hard'} difficulty
 * @returns {number | null}
 */
export function pickAiMove(board, aiPlayer, difficulty) {
  if (difficulty === 'easy') return getEasyMove(board, aiPlayer);
  if (difficulty === 'medium') return getMediumMove(board, aiPlayer);
  return getHardMove(board, aiPlayer);
}
